import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Auth0Client } from '@auth0/auth0-spa-js';
import * as Sentry from '@sentry/react';
import { bumpQueues } from '../stores/queueRefreshBus';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Opt this request out of the queue-refresh-bus bump that every
     * successful mutation normally fires. For background/self-healing writes
     * whose result the caller applies to local state itself (e.g. the Kroll
     * bulk-resync-status POST) — bumping the bus there refetches the whole
     * queue the caller just painted.
     */
    skipQueueBump?: boolean;
  }
}

// Create axios instance
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
    timeout: 120000, // Increased to 2 minutes for PDF processing
    headers: {
        'Content-Type': 'application/json',
    },
});

let auth0Client: Auth0Client | null = null;

// Function to set the Auth0 client
export const setAuth0Client = (client: Auth0Client) => {
    auth0Client = client;
};

// Request interceptor to add auth token
api.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        // Skip auth for public endpoints
        const publicEndpoints = ['/auth/health'];
        const isPublicEndpoint = publicEndpoints.some(endpoint =>
            config.url?.endsWith(endpoint) || config.url === endpoint
        );

        if (!isPublicEndpoint && auth0Client) {
            try {
                // Get access token from Auth0
                const token = await auth0Client.getTokenSilently({
                    // Don't force cache mode off for normal requests
                    detailedResponse: false,
                    timeoutInSeconds: 10
                });
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } catch (error: any) {
                console.error('Failed to get access token:', error);

                // If missing refresh token, try to get token without refresh
                if (error.message?.includes('Missing Refresh Token')) {
                    try {
                        // Try without refresh token - this will use iframe silent auth
                        const token = await auth0Client.getTokenSilently({
                            cacheMode: 'off',
                            detailedResponse: false,
                            timeoutInSeconds: 10
                        });
                        if (token) {
                            config.headers.Authorization = `Bearer ${token}`;
                        }
                    } catch (silentError) {
                        console.error('Silent auth also failed:', silentError);
                        // Continue without token - let the server handle unauthorized
                    }
                }
                // Continue without token - let the server handle unauthorized
            }
        }

        // Add pharmacy header if available (unless explicitly skipped)
        const skipPharmacyFilter = config.headers?.['x-skip-pharmacy-filter'];
        if (!skipPharmacyFilter) {
            // Import store dynamically to avoid circular dependencies
            const { usePharmacyStore } = await import('../stores/pharmacyStore');
            const activePharmacy = usePharmacyStore.getState().activePharmacy;
            if (activePharmacy?.id) {
                config.headers['x-pharmacy-id'] = activePharmacy.id;
            }
        }
        // Remove the skip flag from headers (don't send it to server)
        if (skipPharmacyFilter) {
            delete config.headers['x-skip-pharmacy-filter'];
        }

        // Record request start time for response time metrics
        (config as any)._sentryStartTime = Date.now();

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors and unwrap standardized responses
api.interceptors.response.use(
    (response) => {
        // Track API response time
        const startTime = (response.config as any)._sentryStartTime;
        if (startTime) {
            const duration = Date.now() - startTime;
            Sentry.metrics.distribution('api.response_time', duration, {
                unit: 'millisecond',
                attributes: {
                    endpoint: response.config.url?.replace(/\/\d+/g, '/:id') || 'unknown',
                    method: response.config.method?.toUpperCase() || 'GET',
                    status: String(response.status),
                },
            });
        }

        // Check if response has standardized API format
        if (response.data && typeof response.data === 'object' &&
            'success' in response.data &&
            'data' in response.data &&
            'timestamp' in response.data) {
            // If success, return just the data portion
            if (response.data.success) {
                response.data = response.data.data;
            }
        }

        // Any successful write tickles the queue refresh bus. The active
        // queue's useAutoRefresh subscriber will re-run its onRefresh
        // callback, which keeps both the visible table and the tab badges
        // in step with the latest server state — without each call site
        // having to remember to manually refresh after a mutation. GET is
        // excluded because reads can't change shared state. Auth probes
        // (the public /auth/health and any silent token-refresh handshakes)
        // are excluded so they don't trigger spurious refetches on app
        // boot. Requests sent with `skipQueueBump: true` are excluded —
        // background self-healing writes apply their own result locally and
        // a bump would refetch the very queue they just painted. Failed
        // requests are skipped because the catch arm runs instead.
        const method = response.config.method?.toLowerCase();
        const isMutation = method && method !== 'get' && method !== 'head' && method !== 'options';
        const isAuthProbe = response.config.url?.includes('/auth/');
        if (isMutation && !isAuthProbe && !response.config.skipQueueBump && response.status >= 200 && response.status < 300) {
            bumpQueues();
        }

        return response;
    },
    async (error: AxiosError) => {
        // If request was cancelled, don't process further
        if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
            return Promise.reject(error);
        }

        const originalRequest = error.config as InternalAxiosRequestConfig & {
            _retry?: boolean;
            _retryCount?: number;
        };

        // Maximum retry attempts
        const MAX_RETRY_ATTEMPTS = 1;

        // Handle 403 Forbidden - do NOT retry these
        if (error.response?.status === 403) {
            console.error('Forbidden: You do not have permission to access this resource');
            // Don't retry 403 errors - they won't succeed with a token refresh
            return Promise.reject(error);
        }

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && auth0Client) {
            // Check if we've already retried this request
            originalRequest._retryCount = originalRequest._retryCount || 0;

            if (originalRequest._retryCount >= MAX_RETRY_ATTEMPTS) {
                console.error('Max retry attempts reached for 401 error');
                // Redirect to login after max retries
                await auth0Client.loginWithRedirect({
                    appState: { returnTo: window.location.pathname }
                });
                return Promise.reject(error);
            }

            originalRequest._retryCount++;

            try {
                // Try to refresh the token
                const token = await auth0Client.getTokenSilently({
                    cacheMode: 'off',
                    detailedResponse: false,
                    timeoutInSeconds: 10
                });

                if (token) {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                }
            } catch (refreshError: any) {
                console.error('Token refresh failed:', refreshError);

                // Don't keep retrying - redirect to login
                await auth0Client.loginWithRedirect({
                    appState: { returnTo: window.location.pathname }
                });
                return Promise.reject(refreshError);
            }
        }

        // Handle network errors
        if (!error.response && error.message === 'Network Error') {
            console.error('Network error: Please check your connection');
        }

        // Handle other errors.
        // Deliberately NOT "please try again later": a 500 here is just as often a
        // deterministic server fault that reproduces on every retry, and logging it
        // as transient sent GP-1292 triage looking for a flake for three identical
        // failures. Log what actually failed instead of guessing at the remedy.
        if (error.response?.status === 500) {
            console.error(
                `Server error (500) on ${error.config?.method?.toUpperCase() ?? 'REQUEST'} ${error.config?.url ?? 'unknown URL'} — ` +
                'this may reproduce on retry; check Sentry for the server-side cause.'
            );
        }

        // Capture 5xx errors to Sentry
        if (error.response?.status && error.response.status >= 500) {
            Sentry.captureException(error, {
                extra: {
                    url: error.config?.url,
                    method: error.config?.method,
                    status: error.response.status,
                    statusText: error.response.statusText,
                },
            });
        }

        return Promise.reject(error);
    }
);

// Export configured axios instance
export default api;

// Export commonly used methods
export const { get, post, put, patch, delete: del } = api;

// Export the api instance
export { api };